<template>
  <div class="row">
    <div v-if="readingFromBle" class="loading-overlay">
      <LoadingPlaceholder/>
    </div>
    <div class="col-12 d-flex justify--space-between clickable">
      <h5> {{ $t('checkup_plus.person.measurements.create.data_to_insert') }}</h5>
      <i v-if="isBleAvailable" class="fa-brands fa-bluetooth-b" style="font-size: 30px; color: #223fb9" @click="handleBluetoothRead"></i>
    </div>
    <div class="col-12 d-flex">
      <div class="col-6 form-group">
        <va-date-picker
          class="w-100"
          v-model="form.date"
          :placeholder="$t('iframe.table_headings.created_at')"
          :label="$t('iframe.table_headings.created_at')"
          :config="{
            defaultDate: moment().format('YYYY/MM/DD HH:mm'),
            enableTime: true,
            maxDate: moment(appointmentDates.end).toDate(),
            minDate: moment(appointmentDates.start).toDate(),
            time_24hr: true,
            minuteIncrement: 15,
            dateFormat: 'Y/m/d H:i'}"
        />
      </div>

        <div class="col-6 form-group">
          <va-input
            type="number"
            step="0.1"
            max="300"
            min="0"
            :error="errors.sistolic.length > 0"
            :error-messages="errors.sistolic"
            v-model="form.sistolic"
            :placeholder="$t('dashboard_new_evollu.measurement_types.systolic_short')+' (mmHg)'"
            :label="$t('dashboard_new_evollu.measurement_types.systolic_short')+' (mmHg)'"
            required
            :v-bind:class="{'error': true}"
            :disabled="measuresFromBle"
          />
        </div>

    </div>

    <div class="col-12 d-flex">
      <div class="col-6 form-group">
        <va-input
          type="number"
          step="0.1"
          max="200"
          min="0"
          :error="errors.diastolic.length > 0"
          :error-messages="errors.diastolic"
          v-model="form.diastolic"
          :placeholder="$t('dashboard_new_evollu.measurement_types.diastolic_short')+' (mmHg)'"
          :label="$t('dashboard_new_evollu.measurement_types.diastolic_short')+' (mmHg)'"
          required
          :disabled="measuresFromBle"
        />
      </div>

      <div class="col-6 form-group">
        <va-input
          type="number"
          step="0.1"
          max="300"
          min="0"
          :error="errors.heartRate.length > 0"
          :error-messages="errors.heartRate"
          v-model="form.heartRate"
          :placeholder="$t('dashboard_new_evollu.measurement_types.heart_rate_short')+' (bpm)'"
          :label="$t('dashboard_new_evollu.measurement_types.heart_rate_short')+' (bpm)'"
          required
          :disabled="measuresFromBle"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import HandleBluetoothMixin
  from "@/components/checkup_plus/patients/ParametersDetails/MeasurementsCreation/HandleBluetoothMixin.vue";
import LoadingPlaceholder from "@/components/common/LoadingPlaceholder.vue";

const SYSTOLIC_TYPE_ID = 2
const DIASTOLIC_TYPE_ID = 3
const HEART_RATE_TYPE_ID = 4

export default {
  components: {LoadingPlaceholder},
  props: {
    measurement: {
      type: Object,
      required: true
    },
    appointmentDates: {
      type: Object,
      mandatory: true,
    },
  },
  mixins: [
    HandleBluetoothMixin
  ],
  data() {
    return {
      form: {
        date: this.appointmentDates.start,
        sistolic: null,
        diastolic: null,
        heartRate: null,
        equipmentType: null
      },
      errors: {
        sistolic: [],
        diastolic: [],
        heartRate: []
      }
    }
  },
  created() {
    this.measurement.measurementTypeId = 2
  },
  methods: {
    checkCanSubmit() {
      let canSubmit = (this.form.sistolic !== null && this.form.sistolic !== '') &&
        (this.form.diastolic !== null && this.form.diastolic !== '') &&
        (this.form.heartRate !== null && this.form.heartRate !== '')

        this.$emit('updateCanSubmit', canSubmit)
    }
  },
  watch: {
    "form.sistolic": function(value) {
      this.checkCanSubmit()
      if(value !== '' && (value < 1 || value > 300)) {
        this.errors.sistolic = [this.$t('checkup_plus.person.measurements.create.messages.errors.value_must_be_between_min_max', {min: 1, max: 300})]
      }
      else {
        this.errors.sistolic = []
      }

      if(this.measurement.measurements.find(meas => meas.typeId === SYSTOLIC_TYPE_ID)) {
        let elem = this.measurement.measurements.find(meas => meas.typeId === SYSTOLIC_TYPE_ID)
        elem.value = this.errors.sistolic.length === 0 ? value : null
      } else {
        this.measurement.measurements.push({
          value: this.errors.sistolic.length === 0 ? value : null,
          typeId: SYSTOLIC_TYPE_ID,
          equipmentType: this.form.equipmentType
        })
      }
    },
    "form.diastolic": function(value) {
      this.checkCanSubmit()
      if(value !== '' && (value < 1 || value > 200)) {
        this.errors.diastolic = [this.$t('checkup_plus.person.measurements.create.messages.errors.value_must_be_between_min_max', {min: 1, max: 200})]
      } else {
        this.errors.diastolic = []
      }

      if(this.measurement.measurements.find(meas => meas.typeId === DIASTOLIC_TYPE_ID)) {
        let elem = this.measurement.measurements.find(meas => meas.typeId === DIASTOLIC_TYPE_ID)
        elem.value = this.errors.diastolic.length === 0 ? value : null
      } else {
        this.measurement.measurements.push({
          value: this.errors.diastolic.length === 0 ? value : null,
          typeId: DIASTOLIC_TYPE_ID,
          equipmentType: this.form.equipmentType
        })
      }
    },
    "form.heartRate": function(value) {
      this.checkCanSubmit()
      if(value !== '' && (value < 1 || value > 300)) {
        this.errors.heartRate = [this.$t('checkup_plus.person.measurements.create.messages.errors.value_must_be_between_min_max', {min: 1, max: 300})]
      } else {
        this.errors.heartRate = []
      }

      if(this.measurement.measurements.find(meas => meas.typeId === HEART_RATE_TYPE_ID)) {
        let elem = this.measurement.measurements.find(meas => meas.typeId === HEART_RATE_TYPE_ID)
        elem.value = this.errors.heartRate.length === 0 ? value : null
      } else {
        this.measurement.measurements.push({
          value: this.errors.heartRate.length === 0 ? value : null,
          typeId: HEART_RATE_TYPE_ID,
          equipmentType: this.form.equipmentType
        })
      }
    },
    appointmentDates: {
      immediate: true,
      deep:true,
      handler (value) {
        this.form.date = value.start
        this.measurement.date = moment(value.start).valueOf()
      }
    },
    'form.date': function (value) {
      if(value !== null) {
        this.measurement.date = moment(value).valueOf()
      } else {
        this.measurement.date = null
      }
    }
  }
}
</script>

<style scoped>

</style>
